import type { PackageTemplateDTO } from "@/modules/packages/domain/contracts";
import { computeStackedPackagePeriods } from "@/modules/packages/domain/lib/stacked-package-periods";
import {
  useCheckActivePackageQuery,
  usePackageTemplateQuery,
  usePackageTemplatesQuery,
} from "@/modules/packages/presentation/api/queries";
import type { PrivateLessonTemplateDTO } from "@/modules/private-lessons/domain/contracts";
import type { PrivateLessonInitialScheduleOnSellDTO } from "@/modules/private-lessons/domain/contracts/bulk-schedule";
import {
  useCheckActivePrivateLessonPackageQuery,
  usePrivateLessonTemplateQuery,
  usePrivateLessonTemplatesQuery,
} from "@/modules/private-lessons/presentation/api/queries";
import { mapPrivateLessonFormSessionsToInitialSchedule } from "@/modules/private-lessons/presentation/lib/map-private-lesson-form-sessions-to-initial-schedule";
import {
  BulkScheduleFormSchema,
  bulkSessionRowSchema,
} from "@/modules/private-lessons/presentation/ui/BulkScheduleForm";
import { SellPrivateLessonScheduleOnSellSection } from "@/modules/private-lessons/presentation/ui/SellPrivateLessonScheduleOnSellSection";
import {
  useCreateStudent,
  useStudentQuery,
} from "@/modules/students/presentation/api/queries";
import { StudentForm } from "@/modules/students/presentation/ui/StudentForm";
import { config } from "@/shared/lib/config";
import {
  formatDate,
  normalizeSellPackageStartDateValue,
} from "@/shared/lib/date-time";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import type { SellPackageDiscountType } from "@/shared/ui/SellPackageDiscountTypeRadio";
import { SellPackageDiscountTypeRadio } from "@/shared/ui/SellPackageDiscountTypeRadio";
import { SellPackageSettlementModeRadio } from "@/shared/ui/SellPackageSettlementModeRadio";
import { SellPackageStackedPeriodPreview } from "@/shared/ui/SellPackageStackedPeriodPreview";
import {
  Button,
  Checkbox,
  FormDatePicker,
  FormNumber,
  FormPrice,
  FormRoot,
  FormSelect,
  Label,
  Modal,
  RHF,
  toast,
  zodResolver
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { addDays, format } from "date-fns";
import {
  ArrowRightIcon,
  CalendarIcon,
  CheckIcon,
  PackageIcon,
  UserIcon,
  UserPlusIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";

type StudentLookupItem = { id: string; name: string; memberId: string };

// startDate: preprocess ປ້ອງກັນຄ່າວ່າງ / Invalid Date / string ຈາກ date picker
const SellPackageFormSchema = z
  .object({
    studentId: z.string().min(1, "ຕ້ອງເລືອກນັກຮຽນ"),
    templateId: z.string().min(1, "ຕ້ອງເລືອກແພັກເກດ"),
    /** ສຳລັບ private-lesson ເທົ່ານັ້ນ; ກວດຄືນໃນ handleSubmit */
    purchasedHours: z.number().int().min(1).optional(),
    /** ສຳລັບ private-lesson: ຈຳນວນວັນໃຊ້ແພັກເກດ (ປັບຕອນຂາຍ) */
    validDays: z.number().int().min(1).max(3650).optional(),
    startDate: z.preprocess(normalizeSellPackageStartDateValue, z.date()),
    /** ຈຳນວນຮອບຕໍ່ເນື່ອ (class package) */
    quantity: z.coerce.number().int().min(1).max(24).optional().default(1),
    deactivateExisting: z.boolean().default(false),
    selectedRuleIds: z.array(z.string()).optional(),
    expireDate: z.string().optional(),
    discountType: z.enum(["percentage", "fixed", "none"]).optional(),
    discountValue: z.number().optional(),
    /** receipt = ໃບເກັບເງິນເຕັມ; credit = ໃບເກັບເງິນ + ຊຳລະຕໍ່ */
    settlementMode: z.enum(["receipt", "credit"]).default("receipt"),
    /** ເມື່ອ credit: ຈຳນວນທີ່ຮັບຕອນຂາຍ (0 … ລາຄາສຸດທ້າຍ) */
    amountPaidNow: z.coerce.number().int().min(0).default(0),
    /** private-lesson: ຈອງຕາຕະລາງພ້ອມຂາຍ */
    plScheduleWithSale: z.boolean(),
    plDefaultRoomId: z.string().optional(),
    plInstructorIds: z.array(z.string()).default([]),
    plSessions: z.array(bulkSessionRowSchema).default([]),
  })
  .refine(
    (data) => {
      // ບໍ່ມີສ່ວນຫຼຸດ: ອະນຸຍາດ
      if (!data.discountType || data.discountType === "none") return true;
      // ถ้ามี discountType ต้องมี discountValue
      if (data.discountValue === undefined) return false;
      // ถ้ามี discountValue ต้องมี discountType
      if (!data.discountType) return false;
      // ถ้ามี discountType และ discountValue
      if (data.discountType && data.discountValue !== undefined) {
        // percentage: ต้องอยู่ระหว่าง 0-100
        if (data.discountType === "percentage") {
          return data.discountValue >= 0 && data.discountValue <= 100;
        }
        // fixed: ต้อง > 0
        if (data.discountType === "fixed") {
          return data.discountValue > 0;
        }
      }
      return true;
    },
    {
      message: "ສ່ວນຫຼຸດບໍ່ຖືກຕ້ອງ",
    },
  );
export type SellPackageFormValues = z.infer<typeof SellPackageFormSchema>;

type PackageType = "package" | "private-lesson";

type SellPackageFormProps = {
  packageType: PackageType;
  initialStudentId?: string;
  initialTemplateId?: string;
  initialSelectedRuleIds?: string[];
  onSubmit: (values: {
    studentId: string;
    templateId: string;
    startDate: string;
    quantity?: number;
    deactivateExisting: boolean;
    selectedRuleIds?: string[];
    /** ชื่อคลาสที่เลือก (classTypeName - levelName) สำหรับแสดงใน success page */
    selectedClassNames?: string[];
    discountType?: "percentage" | "fixed";
    discountValue?: number;
    /** private lesson: ຈຳນວນຊົ່ວໂມງທີ່ຊື້ */
    purchasedHours?: number;
    /** private lesson: ຈຳນວນວັນທີ່ໃຊ້ໄດ້ */
    validDays?: number;
    settlementMode: "receipt" | "credit";
    amountPaidNow: number;
    /** ມີເມື່ອຈອງຕາຕະລາງພ້ອມຂາຍແພັກເກດຮຽນສ່ວນຕົວ */
    initialSchedule?: PrivateLessonInitialScheduleOnSellDTO;
  }) => void;
  submitting?: boolean;
};

export function SellPackageForm({
  packageType,
  initialStudentId,
  initialTemplateId,
  initialSelectedRuleIds,
  onSubmit,
  submitting,
}: SellPackageFormProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const qc = useQueryClient();
  const createStudent = useCreateStudent();

  const methods = RHF.useForm<SellPackageFormValues>({
    // @ts-expect-error - zodResolver type mismatch with default() in schema
    resolver: zodResolver(SellPackageFormSchema),
    defaultValues: {
      studentId: initialStudentId || "",
      templateId: initialTemplateId || "",
      purchasedHours: 1,
      startDate: new Date(),
      quantity: 1,
      deactivateExisting: false,
      selectedRuleIds: [],
      discountType: "none",
      discountValue: undefined,
      settlementMode: "receipt",
      amountPaidNow: 0,
      plScheduleWithSale: packageType === "private-lesson",
      plDefaultRoomId: "",
      plInstructorIds: [],
      plSessions: [],
    },
  });

  // Reset templateId when packageType changes
  // useEffect(() => {
  //   methods.setValue("templateId", initialTemplateId || "");
  //   methods.setValue("discountType", undefined);
  //   methods.setValue("discountValue", undefined);
  // }, [packageType, methods]);

  const studentId = methods.watch("studentId");
  const templateId = methods.watch("templateId");
  const purchasedHours = methods.watch("purchasedHours");
  const privateValidDays = methods.watch("validDays");
  const startDate = methods.watch("startDate");
  const discountType = methods.watch("discountType");
  const discountValue = methods.watch("discountValue");
  const quantity = methods.watch("quantity") ?? 1;
  const settlementMode = methods.watch("settlementMode");
  const amountPaidNowW = methods.watch("amountPaidNow");
  const [plScheduleBlocksSubmit, setPlScheduleBlocksSubmit] = useState(false);
  const onPlScheduleBlocksSubmit = useCallback((blocked: boolean) => {
    setPlScheduleBlocksSubmit(blocked);
  }, []);

  // Query student details
  const { data: studentData } = useStudentQuery(studentId || "");

  // Query templates based on package type - always call hooks
  const { data: packageTemplatesData } = usePackageTemplatesQuery(
    packageType === "package"
      ? {
        limit: 100,
        offset: 0,
        filters: [{ field: "status", op: "eq", value: "active" }],
      }
      : { limit: 100, offset: 0 },
  );

  const { data: privateLessonTemplatesData } = usePrivateLessonTemplatesQuery(
    packageType === "private-lesson"
      ? {
        limit: 100,
        offset: 0,
        filters: [
          { field: "status", op: "eq", value: "active" },
          { field: "packageKind", op: "eq", value: "individual" },
        ],
      }
      : { limit: 100, offset: 0 },
  );

  // Query active package for selected student - always call both hooks
  const { data: activePackageDataPackage } = useCheckActivePackageQuery(
    packageType === "package" ? studentId || null : null,
  );
  const { data: activePackageDataPrivateLesson } =
    useCheckActivePrivateLessonPackageQuery(
      packageType === "private-lesson" ? studentId || null : null,
    );
  const activePackageData =
    packageType === "package"
      ? activePackageDataPackage
      : activePackageDataPrivateLesson;

  // Get template detail based on package type - always call both hooks
  const { data: packageTemplateDetail } = usePackageTemplateQuery(
    packageType === "package" && templateId ? templateId : "",
  );

  const { data: privateLessonTemplateDetail } = usePrivateLessonTemplateQuery(
    packageType === "private-lesson" && templateId ? templateId : "",
  );

  const templateOptions = useMemo(() => {
    if (packageType === "package") {
      const templates = packageTemplatesData?.data ?? [];
      return templates.map((t) => ({
        value: t.id,
        label: `${t.name} (${t.totalSessions} ຄັ້ງ, ${t.validDays} ວັນ, ${t.price.toLocaleString()} ກີບ)`,
      }));
    }
    const templates = privateLessonTemplatesData?.data ?? [];
    return templates.map((t) => ({
      value: t.id,
      label: `${t.name} (${t.pricePerHour.toLocaleString("lo-LA")} ₭/ຊົ່ວໂມງ, ${t.validDays} ວັນ)`,
    }));
  }, [packageType, packageTemplatesData, privateLessonTemplatesData]);

  const selectedTemplate = useMemo(() => {
    if (!templateId) return null;
    if (packageType === "package") {
      return (
        packageTemplateDetail ||
        packageTemplatesData?.data.find((t) => t.id === templateId)
      );
    }
    return (
      privateLessonTemplateDetail ||
      privateLessonTemplatesData?.data.find((t) => t.id === templateId)
    );
  }, [
    templateId,
    packageType,
    packageTemplateDetail,
    packageTemplatesData,
    privateLessonTemplateDetail,
    privateLessonTemplatesData,
  ]);

  const plTemplateDefaultLocationKind = useMemo((): "studio" | "off_site" => {
    if (packageType !== "private-lesson" || !selectedTemplate) return "studio";
    return (
      (selectedTemplate as PrivateLessonTemplateDTO).defaultLocationKind ??
      "studio"
    );
  }, [packageType, selectedTemplate]);

  // Calculate expire date from template validDays (package) or form validDays (private)
  useEffect(() => {
    if (selectedTemplate && startDate) {
      try {
        const startDateObj =
          startDate instanceof Date ? startDate : new Date(startDate);
        const days =
          packageType === "private-lesson"
            ? (privateValidDays ??
              (selectedTemplate as PrivateLessonTemplateDTO).validDays)
            : (selectedTemplate as PackageTemplateDTO).validDays;
        const expireDateObj = addDays(startDateObj, days);
        const expireDate = format(expireDateObj, "yyyy-MM-dd");
        methods.setValue("expireDate", expireDate, { shouldValidate: false });
      } catch {
        // Invalid date, ignore
      }
    }
  }, [selectedTemplate, startDate, packageType, privateValidDays, methods]);

  // Reset selectedRuleIds when template changes (preserve camp prefill on first load)
  // biome-ignore lint/correctness/useExhaustiveDependencies: must run when templateId changes
  useEffect(() => {
    if (
      initialSelectedRuleIds?.length &&
      templateId &&
      templateId === initialTemplateId
    ) {
      methods.setValue("selectedRuleIds", initialSelectedRuleIds, {
        shouldValidate: false,
      });
      return;
    }
    methods.setValue("selectedRuleIds", [], { shouldValidate: false });
  }, [templateId, methods, initialSelectedRuleIds, initialTemplateId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when templateId changes
  useEffect(() => {
    if (packageType === "private-lesson" && selectedTemplate) {
      methods.setValue("purchasedHours", 1, { shouldValidate: false });
      methods.setValue(
        "validDays",
        (selectedTemplate as PrivateLessonTemplateDTO).validDays,
        { shouldValidate: false },
      );
    }
  }, [templateId, packageType, selectedTemplate, methods]);

  const expireDate = methods.watch("expireDate");
  const hasActivePackage =
    packageType === "package"
      ? (activePackageData?.hasActivePackage ?? false)
      : (activePackageData?.hasActivePackage ?? false);
  const activePackage = activePackageData?.activePackage;

  // Calculate discount and prices
  const priceCalculation = useMemo(() => {
    if (!selectedTemplate) {
      return {
        originalPrice: 0,
        discountAmount: 0,
        finalPrice: 0,
      };
    }

    let originalPrice: number;
    if (packageType === "package") {
      const qty = quantity >= 1 ? quantity : 1;
      originalPrice = (selectedTemplate as PackageTemplateDTO).price * qty;
    } else {
      const template = selectedTemplate as PrivateLessonTemplateDTO;
      const hours = purchasedHours ?? 1;
      originalPrice = hours * template.pricePerHour;
    }

    let discountAmount = 0;
    let finalPrice = originalPrice;

    if (
      (discountType === "percentage" || discountType === "fixed") &&
      discountValue !== undefined
    ) {
      if (discountType === "percentage") {
        discountAmount = Math.round((originalPrice * discountValue) / 100);
      } else if (discountType === "fixed") {
        discountAmount = Math.round(discountValue);
      }

      // Ensure discount doesn't exceed original price
      discountAmount = Math.min(discountAmount, originalPrice);
      finalPrice = originalPrice - discountAmount;

      // Ensure final price is not negative
      if (finalPrice < 0) {
        finalPrice = 0;
        discountAmount = originalPrice;
      }
    }

    return {
      originalPrice,
      discountAmount,
      finalPrice,
    };
  }, [
    selectedTemplate,
    packageType,
    purchasedHours,
    discountType,
    discountValue,
    quantity,
  ]);

  const stackedPeriods = useMemo(() => {
    if (
      packageType !== "package" ||
      !selectedTemplate ||
      quantity <= 1 ||
      !startDate
    ) {
      return [];
    }
    const startDateStr =
      startDate instanceof Date
        ? format(startDate, "yyyy-MM-dd")
        : format(new Date(startDate), "yyyy-MM-dd");
    return computeStackedPackagePeriods({
      firstStartDate: startDateStr,
      validDays: (selectedTemplate as PackageTemplateDTO).validDays,
      quantity,
    });
  }, [packageType, selectedTemplate, quantity, startDate]);

  const stackedSummaryExpireDate =
    stackedPeriods.length > 0
      ? stackedPeriods[stackedPeriods.length - 1]?.expireDate
      : undefined;

  useEffect(() => {
    if (packageType !== "private-lesson") {
      setPlScheduleBlocksSubmit(false);
    }
  }, [packageType]);

  const usageStepNum = packageType === "private-lesson" ? 5 : 3;
  const summaryStepNum = packageType === "private-lesson" ? 6 : 4;

  const handleSubmit = (values: SellPackageFormValues) => {
    let initialSchedule: PrivateLessonInitialScheduleOnSellDTO | undefined;

    if (packageType === "private-lesson") {
      const ph = values.purchasedHours;
      if (ph === undefined || !Number.isInteger(ph) || ph < 1) {
        methods.setError("purchasedHours", {
          type: "manual",
          message: "ກະລຸນາປ້ອນຈຳນວນຊົ່ວໂມງ (ຈຳນວນເຕັມ ≥ 1)",
        });
        return;
      }
      const vd = values.validDays;
      if (vd === undefined || !Number.isInteger(vd) || vd < 1 || vd > 3650) {
        methods.setError("validDays", {
          type: "manual",
          message: "ກະລຸນາປ້ອນຈຳນວນວັນ (1–3650)",
        });
        return;
      }

      if (values.plScheduleWithSale) {
        const parsed = BulkScheduleFormSchema.safeParse({
          defaultRoomId: values.plDefaultRoomId,
          instructorIds: values.plInstructorIds ?? [],
          sessions: values.plSessions ?? [],
        });
        if (!parsed.success) {
          toast.error(parsed.error.issues[0]?.message ?? "ກຳນົດຕາຕະລາງບໍ່ຖືກຕ້ອງ");
          return;
        }
        try {
          initialSchedule = mapPrivateLessonFormSessionsToInitialSchedule(
            parsed.data.instructorIds,
            parsed.data.sessions,
          );
        } catch (e) {
          toast.error(e instanceof Error ? e.message : "ກຳນົດຕາຕະລາງບໍ່ຖືກຕ້ອງ");
          return;
        }
      }
    }

    const pkgTemplate =
      packageType === "package"
        ? (selectedTemplate as PackageTemplateDTO | null)
        : null;
    const maxSelectable = pkgTemplate?.maxSelectableClasses;
    const ruleIds = values.selectedRuleIds ?? [];
    if (
      maxSelectable != null &&
      maxSelectable >= 1 &&
      (ruleIds.length !== maxSelectable ||
        !ruleIds.every((id) => pkgTemplate?.rules?.some((r) => r.id === id)))
    ) {
      methods.setError("selectedRuleIds", {
        type: "manual",
        message: `ກະລຸນາເລືອກຄລາດສ໌ຢ່າງໜ້ອຍ ${maxSelectable} ຄລາດສ໌ຈາກລາຍການ`,
      });
      return;
    }
    const startDateValue =
      values.startDate instanceof Date
        ? values.startDate
        : new Date(values.startDate);
    const selectedClassNames =
      pkgTemplate?.rules && ruleIds.length > 0
        ? ruleIds
          .map((rid) => {
            const r = pkgTemplate.rules?.find((x) => x.id === rid);
            return r ? `${r.classTypeName} - ${r.levelName}` : null;
          })
          .filter((s): s is string => s != null)
        : undefined;
    const dt =
      values.discountType && values.discountType !== "none"
        ? values.discountType
        : undefined;
    const dv = dt ? values.discountValue : undefined;

    const fp = priceCalculation.finalPrice;
    if (values.settlementMode === "credit" && fp > 0) {
      const apn = values.amountPaidNow ?? 0;
      if (apn > fp) {
        methods.setError("amountPaidNow", {
          type: "manual",
          message: "ບໍ່ເກີນລາຄາສຸດທ້າຍ",
        });
        return;
      }
    }

    onSubmit({
      studentId: values.studentId,
      templateId: values.templateId,
      startDate: format(startDateValue, "yyyy-MM-dd"),
      quantity:
        packageType === "package" ? Math.max(1, values.quantity ?? 1) : undefined,
      deactivateExisting:
        packageType === "package" && (values.quantity ?? 1) > 1
          ? false
          : values.deactivateExisting,
      selectedRuleIds:
        maxSelectable != null && maxSelectable >= 1 ? ruleIds : undefined,
      selectedClassNames,
      discountType: dt,
      discountValue: dv,
      purchasedHours:
        packageType === "private-lesson" ? values.purchasedHours : undefined,
      validDays:
        packageType === "private-lesson" ? values.validDays : undefined,
      settlementMode: values.settlementMode,
      amountPaidNow:
        values.settlementMode === "credit"
          ? Math.min(Math.max(0, values.amountPaidNow ?? 0), fp)
          : 0,
      initialSchedule:
        packageType === "private-lesson" ? initialSchedule : undefined,
    });
  };

  const handleCreateStudent = async (
    values: Parameters<typeof createStudent.mutateAsync>[0],
  ) => {
    try {
      const result = await createStudent.mutateAsync(values);
      // Preload the newly created student item for the combobox
      await hydrateLookupItem<StudentLookupItem>(
        `${config.apiUrl}/students/lookup`,
        result.id,
      );
      // Invalidate student lookup query to refresh the list
      qc.invalidateQueries({ queryKey: ["students", "lookup"] });
      // Set the newly created student as selected
      methods.setValue("studentId", result.id, { shouldValidate: true });
      // Close modal
      setIsCreateModalOpen(false);
    } catch {
      // Error handling is done by the mutation
    }
  };

  const activePackageWarningText =
    packageType === "package"
      ? `ນັກຮຽນມີແພັກເກດທີ່ໃຊ້ງານຢູ່ ${activePackage?.templateName || ""} (${(activePackage as { remainingSessions?: number })
        ?.remainingSessions || 0
      } ຄັ້ງທີເຫຼືອ).`
      : `ນັກຮຽນມີແພັກເກດຮຽນສ່ວນຕົວທີ່ໃຊ້ງານຢູ່ ${activePackage?.templateName || ""} (${(activePackage as { remainingHours?: number })?.remainingHours || 0
      } ຊົ່ວໂມງທີເຫຼືອ).`;

  return (
    // @ts-expect-error - FormRoot type mismatch
    <FormRoot methods={methods} onSubmit={handleSubmit}>
      <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
        {/* Left Column: Steps 1, 2, 3 */}
        <div className="space-y-6">
          {/* Step 1: Student Selection */}
          <div className="rounded-xl border bg-card p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex size-8 items-center justify-center rounded-full bg-red-600 font-bold text-sm text-white">
                1
              </div>
              <h3 className="font-semibold text-lg">ເລືອກນັກຮຽນ</h3>
              <span className="ml-auto rounded bg-red-100 px-2 py-1 font-medium text-red-700 text-xs">
                ຈຳເປັນ
              </span>
            </div>

            <div className="space-y-4">
              <div className="flex w-full flex-col items-end gap-2 md:flex-row">
                <FormInfiniteCombobox<StudentLookupItem>
                  name="studentId"
                  label="ກຳນົດນັກຮຽນ"
                  requiredMark
                  queryKey={["students", "lookup"]}
                  queryFn={(args) =>
                    fetchLookupForInfinite<StudentLookupItem>(
                      `${config.apiUrl}/students/lookup`,
                      args,
                    )
                  }
                  preloadQueryFn={(id) =>
                    hydrateLookupItem<StudentLookupItem>(
                      `${config.apiUrl}/students/lookup`,
                      id,
                    )
                  }
                  getLabel={(item) => `${item.memberId} - ${item.name}`}
                  getValue={(item) => item.id}
                  placeholder="ຄົ້ນຫານັກຮຽນຕາມຊື່ຫຼືເລກສະມາຊິກ..."
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9"
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  <UserPlusIcon className="mr-2 size-4" />
                  ລົງທະບຽນນັກຮຽນໃໝ່
                </Button>
              </div>
            </div>
          </div>

          {/* Step 2: Package Selection */}
          <div className="rounded-xl border bg-card p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex size-8 items-center justify-center rounded-full bg-red-600 font-bold text-sm text-white">
                2
              </div>
              <h3 className="font-semibold text-lg">ເລືອກແພັກເກດ</h3>
              <span className="ml-auto rounded bg-gray-100 px-2 py-1 font-medium text-gray-700 text-xs dark:bg-gray-800 dark:text-gray-300">
                ແມ່ແບບແພັກເກດ
              </span>
            </div>

            <div className="space-y-4">
              <FormSelect
                name="templateId"
                label="ເລືອກແມ່ແບບແພັກເກດ"
                requiredMark
                options={templateOptions}
                placeholder="ເລືອກແມ່ແບບແພັກເກດ..."
              />

              {packageType === "private-lesson" && selectedTemplate && (
                <div className="grid gap-4 md:grid-cols-2">
                  <FormNumber
                    name="purchasedHours"
                    label="ຈຳນວນຊົ່ວໂມງທີ່ຊື້"
                    placeholder="ປ້ອນຈຳນວນຊົ່ວໂມງ"
                    requiredMark
                    min={1}
                    step={1}
                  />
                  <FormNumber
                    name="validDays"
                    label="ຈຳນວນວັນທີ່ໃຊ້ໄດ້ (ນັບຈາກວັນເລີ່ມ)"
                    placeholder="ປ້ອນຈຳນວນວັນ"
                    requiredMark
                    min={1}
                    max={3650}
                    step={1}
                  />
                  <p className="text-muted-foreground text-xs">
                    ຄ່າເລີ່ມຕົ້ນຈາກແມ່ແບບ (
                    {(selectedTemplate as PrivateLessonTemplateDTO).validDays}{" "}
                    ວັນ) — ປັບໄດ້ຕາມການຂາຍ.
                  </p>
                </div>
              )}

              {/* Template Summary Card */}
              {selectedTemplate && (
                <div className="rounded-lg border-2 border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/20">
                  <div className="mb-3 flex items-center gap-2">
                    <h4 className="font-semibold">
                      ສະຫຼຸບແມ່ແບບ: {selectedTemplate.name.toUpperCase()}
                    </h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {packageType === "package" ? (
                      <>
                        <div>
                          <span className="text-muted-foreground">
                            ຈຳນວນຄັ້ງທັງໝົດ:
                          </span>
                          <p className="font-bold">
                            {(selectedTemplate as PackageTemplateDTO)
                              .totalSessions === -1
                              ? "ບໍ່ຈຳກັດ"
                              : (selectedTemplate as PackageTemplateDTO)
                                .totalSessions}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            ຊ່ວງເວລາທີ່ໃຊ້ໄດ້:
                          </span>
                          <p className="font-bold">
                            {selectedTemplate.validDays} ວັນ
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            ລາຄາພື້ນຖານ:
                          </span>
                          <p className="font-bold text-red-600">
                            {(
                              selectedTemplate as PackageTemplateDTO
                            ).price.toLocaleString()}{" "}
                            ກີບ
                          </p>
                        </div>
                        {(() => {
                          const templateRules = (
                            selectedTemplate as PackageTemplateDTO
                          ).rules;
                          return templateRules && templateRules.length > 0;
                        })() && (
                            <div className="col-span-2">
                              <span className="text-muted-foreground">
                                ກົດລະບຽບການໃຊ້ງານທີ່ອະນຸຍາດ:
                              </span>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {(
                                  (selectedTemplate as PackageTemplateDTO)
                                    .rules || []
                                ).map((rule) => (
                                  <span
                                    key={`${rule.classTypeId}-${rule.levelId}`}
                                    className="rounded bg-white px-2 py-1 font-medium text-gray-700 text-xs dark:bg-gray-800 dark:text-gray-300"
                                  >
                                    {rule.classTypeName} - {rule.levelName}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                      </>
                    ) : (
                      <>
                        <div>
                          <span className="text-muted-foreground">
                            ຫນ່ວຍແມ່ແບບ:
                          </span>
                          <p className="font-bold">1 ຊົ່ວໂມງຕໍ່ຫນ່ວຍລາຄາ</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            ຊ່ວງເວລາທີ່ໃຊ້ໄດ້ (ຂາຍ):
                          </span>
                          <p className="font-bold">
                            {privateValidDays ??
                              (selectedTemplate as PrivateLessonTemplateDTO)
                                .validDays}{" "}
                            ວັນ
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            ລາຄາຕໍ່ຊົ່ວໂມງ:
                          </span>
                          <p className="font-bold text-red-600">
                            {(
                              selectedTemplate as PrivateLessonTemplateDTO
                            ).pricePerHour.toLocaleString("lo-LA")}{" "}
                            ກີບ
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            ຍອດກ່ອນສ່ວນຫຼຸດ:
                          </span>
                          <p className="font-bold text-red-600">
                            {(
                              (purchasedHours ?? 1) *
                              (selectedTemplate as PrivateLessonTemplateDTO)
                                .pricePerHour
                            ).toLocaleString("lo-LA")}{" "}
                            ກີບ
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Class Selection - package type only, when template has maxSelectableClasses */}
              {packageType === "package" &&
                (() => {
                  const pkgTpl = selectedTemplate as PackageTemplateDTO | null;
                  return (
                    pkgTpl?.maxSelectableClasses != null &&
                    pkgTpl.maxSelectableClasses >= 1 &&
                    pkgTpl.rules &&
                    pkgTpl.rules.length > 0
                  );
                })() && (
                  <div className="space-y-3 rounded-lg border p-4">
                    <div>
                      <span className="block font-medium text-sm">
                        ເລືອກຄລາດສ໌ທີ່ຈະໃຊ້ແພັກເກດ
                        <span className="text-destructive"> *</span>
                      </span>
                      <p className="text-muted-foreground text-xs">
                        ເລືອກຢ່າງໜ້ອຍ{" "}
                        {
                          (selectedTemplate as PackageTemplateDTO)
                            .maxSelectableClasses
                        }{" "}
                        ຄລາດສ໌ (ເລືອກໄດ້{" "}
                        {
                          (selectedTemplate as PackageTemplateDTO)
                            .maxSelectableClasses
                        }{" "}
                        ຄລາດສ໌ເທົ່ານັ້ນ)
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {(
                        (selectedTemplate as PackageTemplateDTO).rules || []
                      ).map((rule) => {
                        const selectedIds =
                          methods.watch("selectedRuleIds") ?? [];
                        const isChecked = selectedIds.includes(rule.id);
                        const maxSel =
                          (selectedTemplate as PackageTemplateDTO)
                            .maxSelectableClasses ?? 1;
                        const isDisabled =
                          !isChecked && selectedIds.length >= maxSel;
                        return (
                          <Label
                            key={rule.id}
                            htmlFor={`rule-${rule.id}`}
                            className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${isChecked
                                ? "border-red-600 bg-red-50 dark:bg-red-950/20"
                                : isDisabled
                                  ? "cursor-not-allowed opacity-50"
                                  : "hover:bg-muted/50"
                              }`}
                          >
                            <Checkbox
                              id={`rule-${rule.id}`}
                              checked={isChecked}
                              disabled={isDisabled}
                              onCheckedChange={(checked) => {
                                const current =
                                  methods.getValues("selectedRuleIds") ?? [];
                                if (checked) {
                                  methods.setValue("selectedRuleIds", [
                                    ...current,
                                    rule.id,
                                  ]);
                                } else {
                                  methods.setValue(
                                    "selectedRuleIds",
                                    current.filter((id) => id !== rule.id),
                                  );
                                }
                              }}
                            />
                            <span>
                              {rule.classTypeName} - {rule.levelName}
                            </span>
                          </Label>
                        );
                      })}
                    </div>
                    {methods.formState.errors.selectedRuleIds && (
                      <p className="text-destructive text-sm">
                        {methods.formState.errors.selectedRuleIds.message}
                      </p>
                    )}
                  </div>
                )}
            </div>
          </div>

          {packageType === "private-lesson" && (
            <SellPrivateLessonScheduleOnSellSection
              methods={methods}
              purchasedHours={purchasedHours ?? 1}
              templateDefaultLocationKind={plTemplateDefaultLocationKind}
              onScheduleBlocksSubmit={onPlScheduleBlocksSubmit}
            />
          )}

          {/* Step 3: Usage Period */}
          <div className="rounded-xl border bg-card p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex size-8 items-center justify-center rounded-full bg-red-600 font-bold text-sm text-white">
                {usageStepNum}
              </div>
              <h3 className="font-semibold text-lg">ໄລຍະເວລາການໃຊ້ງານ</h3>
            </div>

            <p className="mb-4 text-muted-foreground text-sm">
              {packageType === "private-lesson"
                ? "ວັນຫມົດອາຍຸ = ວັນເລີ່ມ + ຈຳນວນວັນທີ່ຕັ້ງຕອນຂາຍ (ແມ່ແບບໃນຂັ້ນຕອນ 2)."
                : "ວັນທີ່ຫມົດອາຍຸຖືກຄິດໄລ່ອັດຕະໂນມັດຕາມກົດລະບຽບແມ່ແບບແພັກເກດ."}
            </p>

            <div className="space-y-4">
              <div className="space-y-2">
                <FormDatePicker
                  name="startDate"
                  label="ວັນທີເລີ່ມຕົ້ນ"
                  requiredMark
                  placeholder="ເລືອກວັນທີເລີ່ມຕົ້ນ..."
                />
                <p className="text-muted-foreground text-xs">
                  ມື້ນີ້ (ຕັ້ງເປັນຄ່າເລີ່ມຕົ້ນ)
                </p>
              </div>

              {packageType === "package" && (
                <div className="space-y-2">
                  <FormNumber
                    name="quantity"
                    label="ຈຳນວນຮອບຕໍ່ເນື່ອ"
                    min={1}
                    max={24}
                    requiredMark
                  />
                  <p className="text-muted-foreground text-xs">
                    ຊື້ແພັກເກດດຽວກັນຫຼາຍຮອບ — ວັນເລີ່ມຕໍ່ເນື່ອອັດຕະໂນມັດ (ສູງສຸດ 24 ຮອບ)
                  </p>
                </div>
              )}

              {packageType === "package" &&
                stackedPeriods.length > 1 &&
                selectedTemplate && (
                  <SellPackageStackedPeriodPreview
                    periods={stackedPeriods}
                    sessionsPerPeriod={
                      (selectedTemplate as PackageTemplateDTO).totalSessions
                    }
                    pricePerPeriod={
                      (selectedTemplate as PackageTemplateDTO).price
                    }
                    totalSessions={
                      (selectedTemplate as PackageTemplateDTO).totalSessions *
                      quantity
                    }
                    totalPrice={priceCalculation.finalPrice}
                  />
                )}

              {expireDate && quantity <= 1 && (
                <div className="space-y-2">
                  <label
                    htmlFor="expire-date-display"
                    className="block font-medium text-sm"
                  >
                    ວັນທີ່ຫມົດອາຍຸ
                  </label>
                  <div
                    id="expire-date-display"
                    className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2"
                  >
                    <CalendarIcon className="size-4 text-muted-foreground" />
                    <span className="flex-1 text-sm">
                      {format(new Date(expireDate), "MMM dd, yyyy")}
                    </span>
                    {selectedTemplate && (
                      <span className="rounded bg-red-100 px-2 py-1 font-medium text-red-700 text-xs">
                        +
                        {packageType === "private-lesson"
                          ? (privateValidDays ??
                            (selectedTemplate as PrivateLessonTemplateDTO)
                              .validDays)
                          : selectedTemplate.validDays}{" "}
                        Days
                      </span>
                    )}
                  </div>
                  <p className="text-muted-foreground text-xs">
                    ຄ່າທີ່ຄິດໄລ່ອັດຕະໂນມັດ
                  </p>
                </div>
              )}

              {/* Discount Section */}
              <div className="space-y-3 border-t pt-4">
                <RHF.Controller
                  name="discountType"
                  control={methods.control}
                  render={({ field }) => (
                    <SellPackageDiscountTypeRadio
                      value={
                        (field.value as SellPackageDiscountType | undefined) ??
                        "none"
                      }
                      onChange={(next) => {
                        field.onChange(next);
                        if (next === "none") {
                          methods.setValue("discountValue", undefined, {
                            shouldValidate: false,
                          });
                        }
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                    />
                  )}
                />

                {(discountType === "percentage" ||
                  discountType === "fixed") && (
                    <div className="max-w-sm">
                      {discountType === "percentage" ? (
                        <FormNumber
                          name="discountValue"
                          label="ສ່ວນຫຼຸດ (%)"
                          placeholder="ປ້ອນສ່ວນຫຼຸດ (0-100)"
                          min={0}
                          max={100}
                        />
                      ) : (
                        <FormPrice
                          name="discountValue"
                          label="ສ່ວນຫຼຸດ (ກີບ)"
                          placeholder="ປ້ອນຈຳນວນເງິນ"
                          currencySymbol="₭"
                        />
                      )}
                    </div>
                  )}
              </div>

              <div className="grid gap-2 border-t pt-4">
                <RHF.Controller
                  name="settlementMode"
                  control={methods.control}
                  render={({ field }) => (
                    <SellPackageSettlementModeRadio
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                    />
                  )}
                />
                {settlementMode === "credit" &&
                  priceCalculation.finalPrice > 0 && (
                    <FormPrice
                      name="amountPaidNow"
                      label="ຈຳນວນທີ່ຮັບຕອນຂາຍ — 0 ຖ້າຍັງບໍ່ຮັບ"
                      placeholder="0"
                      currencySymbol="₭"
                      min={0}
                      max={priceCalculation.finalPrice}
                      step={1}
                    />
                  )}
                {settlementMode === "credit" &&
                  priceCalculation.finalPrice <= 0 && (
                    <p className="text-muted-foreground text-xs">
                      ລາຄາສຸດທ້າຍເປັນ 0 — ບໍ່ສ້າງໃບເກັບເງິນ; ແພັກເກດເປີດໃຊ້ງານຕາມປົກກະຕິ
                    </p>
                  )}
              </div>

              {selectedTemplate && priceCalculation && (
                <div className="rounded-lg border bg-muted/50 p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ຍອດເງິນ:</span>
                    <span className="font-semibold">
                      {priceCalculation.originalPrice.toLocaleString("lo-LA")}{" "}
                      ກີບ
                    </span>
                  </div>
                  {priceCalculation.discountAmount > 0 && (
                    <div className="mt-1 flex justify-between text-green-600">
                      <span>ສ່ວນຫຼຸດ:</span>
                      <span className="font-semibold">
                        -
                        {priceCalculation.discountAmount.toLocaleString(
                          "lo-LA",
                        )}{" "}
                        ກີບ
                      </span>
                    </div>
                  )}
                  <div className="mt-2 flex justify-between border-t pt-2">
                    <span className="font-semibold">ລາຄາສຸດທ້າຍ:</span>
                    <span className="font-bold text-red-600">
                      {priceCalculation.finalPrice.toLocaleString("lo-LA")} ກີບ
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Order Summary (Sticky) */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <div className="rounded-xl border border-l-4 border-l-red-600 bg-card p-6 shadow-lg">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex size-8 items-center justify-center rounded-full bg-red-600 font-bold text-sm text-white">
                {summaryStepNum}
              </div>
              <h3 className="font-semibold text-lg">ສະຫຼຸບຄຳສັ່ງຊື້</h3>
            </div>

            <div className="space-y-4">
              {/* Student Information */}
              {studentData && (
                <div className="flex items-start gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                    <UserIcon className="size-4 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-muted-foreground text-xs uppercase">
                      ນັກຮຽນ
                    </p>
                    <p className="font-semibold">{studentData.name}</p>
                    <p className="text-muted-foreground text-sm">
                      ເລກສະມາຊິກ: {studentData.memberId}
                    </p>
                  </div>
                </div>
              )}

              {/* Package Information */}
              {selectedTemplate && (
                <div className="flex items-start gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                    <PackageIcon className="size-4 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-muted-foreground text-xs uppercase">
                      ແພັກເກດ
                    </p>
                    <p className="font-semibold">{selectedTemplate.name}</p>
                    <p className="text-muted-foreground text-sm">
                      ຖືກຕ້ອງສຳລັບ{" "}
                      {packageType === "private-lesson"
                        ? (privateValidDays ??
                          (selectedTemplate as PrivateLessonTemplateDTO)
                            .validDays)
                        : selectedTemplate.validDays}{" "}
                      ວັນນັບຈາກການເປີດໃຊ້ງານ
                    </p>
                  </div>
                </div>
              )}

              {/* Timeline */}
              {startDate &&
                (stackedSummaryExpireDate || expireDate) &&
                packageType === "package" &&
                quantity > 1 && (
                  <div className="flex items-start gap-3">
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                      <CalendarIcon className="size-4 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-muted-foreground text-xs uppercase">
                        ໄລຍະເວລາລວມ ({quantity} ຮອບ)
                      </p>
                      <p className="font-semibold">
                        {formatDate(startDate)}{" "}
                        <ArrowRightIcon className="inline size-4" />{" "}
                        {formatDate(stackedSummaryExpireDate ?? expireDate)}
                      </p>
                    </div>
                  </div>
                )}
              {startDate && expireDate && !(packageType === "package" && quantity > 1) && (
                <div className="flex items-start gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                    <CalendarIcon className="size-4 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-muted-foreground text-xs uppercase">
                      ໄລຍະເວລາ
                    </p>
                    <p className="font-semibold">
                      {formatDate(startDate)}{" "}
                      <ArrowRightIcon className="inline size-4" />{" "}
                      {formatDate(expireDate)}
                    </p>
                  </div>
                </div>
              )}

              {/* Total Amount Due */}
              {selectedTemplate && (
                <div className="border-t pt-4">
                  <p className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                    ຈຳນວນເງິນທີ່ຕ້ອງຊຳລະທັງໝົດ
                  </p>
                  {priceCalculation.discountAmount > 0 ? (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">ຍອດເງິນ:</span>
                        <span>
                          {priceCalculation.originalPrice.toLocaleString(
                            "lo-LA",
                          )}{" "}
                          ກີບ
                        </span>
                      </div>
                      <div className="flex justify-between text-green-600 text-sm">
                        <span>ສ່ວນຫຼຸດ:</span>
                        <span className="font-semibold">
                          -
                          {priceCalculation.discountAmount.toLocaleString(
                            "lo-LA",
                          )}{" "}
                          ກີບ
                        </span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="font-semibold">ລາຄາສຸດທ້າຍ:</span>
                        <span className="font-bold text-3xl text-red-600">
                          {priceCalculation.finalPrice.toLocaleString("lo-LA")}{" "}
                          ກີບ
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 font-bold text-3xl text-red-600">
                      {priceCalculation.originalPrice.toLocaleString("lo-LA")}{" "}
                      ກີບ
                    </p>
                  )}
                </div>
              )}

              {settlementMode === "credit" &&
                selectedTemplate &&
                priceCalculation.finalPrice > 0 && (
                  <p className="text-muted-foreground text-xs">
                    ຫຼັງຂາຍ: ຈັດການຊຳລະຕໍ່ທີ່{" "}
                    <span className="font-medium text-foreground">
                      ໃບເກັບເງິນ / ຊຳລະບາງສ່ວນ
                    </span>
                    {` · ຮັບແລ້ວຕອນນີ້ ${(amountPaidNowW ?? 0).toLocaleString("lo-LA")} ກີບ`}
                  </p>
                )}

              {/* Action Buttons */}
              <div className="space-y-3 border-t pt-4">
                <Button
                  type="submit"
                  className="w-full bg-red-600 hover:bg-red-700"
                  disabled={
                    submitting ||
                    (packageType === "private-lesson" &&
                      methods.watch("plScheduleWithSale") &&
                      plScheduleBlocksSubmit)
                  }
                >
                  <CheckIcon className="mr-2 size-4" />
                  {submitting ? "ກຳລັງຂາຍ..." : "ຢືນຢັນແລະຂາຍແພັກເກດ"}
                </Button>
                <p className="text-muted-foreground text-xs">
                  ໂດຍການກົດຢືນຢັນ,
                  ທ່ານຕົກລົງກັບເງື່ອນໄຂທຸລະກິດແລະແພັກເກດຈະເປີດໃຊ້ງານທັນທີສຳລັບນັກຮຽນ.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Student Modal */}
      <Modal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        title="ລົງທະບຽນນັກຮຽນໃໝ່"
        size="md"
        className="max-h-[85vh] overflow-y-auto"
      >
        <StudentForm
          onSubmit={handleCreateStudent}
          submitting={createStudent.isPending}
        />
      </Modal>
    </FormRoot>
  );
}
